<template>
  <b-modal
      id="similar-contracts-modal"
      ref="modal"
      size="xl"
      centered
      scrollable
      :title="'Contrats similaires'"
      @show="onShow"
      @hidden="reset"
  >
    <div class="mb-4 d-flex align-items-center gap-4 flex-wrap">
      <b-form-checkbox v-model="byIdentifiers" @change="onCriteriaChange">Mêmes identifiers</b-form-checkbox>
      <b-form-checkbox v-model="byMobile" @change="onCriteriaChange">Même mobile souscripteur</b-form-checkbox>
      <b-form-checkbox v-model="includeMerged" @change="onCriteriaChange">Inclure les déjà mergés</b-form-checkbox>
      <div class="ms-auto text-muted small">Master : <code>{{ contractUuid }}</code></div>
    </div>

    <div v-if="!byIdentifiers && !byMobile" class="alert alert-info">
      Sélectionnez au moins un critère pour afficher les contrats similaires.
    </div>
    <div v-else-if="loading" class="text-center py-10">
      <div class="spinner-border text-primary"></div>
      <div class="mt-3">Recherche...</div>
    </div>
    <div v-else>
      <div v-if="!candidates.length" class="alert alert-info">
        Aucun contrat similaire trouvé.
      </div>
      <div v-else>
        <div class="table-responsive">
          <table class="table table-hover align-middle">
            <thead class="bg-light">
            <tr>
              <th style="width:40px">
                <input type="checkbox" :checked="allSelected" @change="toggleAll"/>
              </th>
              <th>Police</th>
              <th>Statut</th>
              <th>Date effet</th>
              <th>Émission</th>
              <th>Expiration</th>
              <th>Identifiers</th>
              <th>Souscripteur</th>
              <th>État</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="c in candidates" :key="c.uuid"
                :class="{'table-primary': isSelected(c.uuid), 'table-secondary': c.deleted}">
              <td>
                <input type="checkbox"
                       :value="c.uuid"
                       v-model="selectedSources"
                       :disabled="c.deleted"/>
              </td>
              <td>
                <router-link :to="{name:'contract-show', params:{contractUuid: c.uuid}}" target="_blank">
                  <code>{{ c.police || c.uuid }}</code>
                </router-link>
              </td>
              <td><span class="badge badge-light">{{ c.status || '—' }}</span></td>
              <td>{{ formatDate(c.dateEffet) }}</td>
              <td>{{ formatDate(c.dateEmission) }}</td>
              <td>{{ formatDate(c.expireAt) }}</td>
              <td>
                <span v-for="id in truncateIds(c.identifiers)" :key="id" class="badge badge-light-primary me-1">{{ id }}</span>
                <span v-if="c.identifiers && c.identifiers.length > 2" class="text-muted small">+{{ c.identifiers.length - 2 }} autres</span>
              </td>
              <td>
                <div>{{ c.subscriber && c.subscriber.name ? c.subscriber.name : '—' }}</div>
                <div class="text-muted small">{{ c.subscriber && c.subscriber.mobile ? c.subscriber.mobile : '' }}</div>
              </td>
              <td>
                <span v-if="c.duplication || c.redirect" class="badge badge-light-danger me-1">Déjà mergé</span>
                <span v-if="c.deleted" class="badge badge-light-dark me-1">Supprimé</span>
                <span v-if="c.isConverted" class="badge badge-light-success">Converti</span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
        <CustomPagination
            v-if="total > perPage"
            :total="total"
            :items-per-page="perPage"
            :current-page="page"
            :item-count="candidates.length"
            :match-count="total"
            @update:currentPage="onPageChange"/>
      </div>
    </div>

    <template #modal-footer="{ cancel }">
      <button class="btn btn-light" @click="cancel">Annuler</button>
      <button class="btn btn-primary"
              :disabled="!canMerge || merging"
              @click="confirmMerge">
        <span v-if="merging" class="spinner-border spinner-border-sm me-2"></span>
        <i v-else class="fad fa-code-merge me-1"></i>
        Fusionner {{ selectedSources.length }} contrat(s) dans ce master
      </button>
    </template>
  </b-modal>
</template>

<script lang="ts">
import {Component, Prop, Vue} from "vue-property-decorator";
import {api} from "@/services/Api";
import Swal from "sweetalert2";
import CustomPagination from "@/components/CustomPagination.vue";

@Component({components: {CustomPagination}})
export default class SimilarContractsModal extends Vue {
  @Prop({required: true}) contractUuid!: string;

  loading = false;
  merging = false;
  byIdentifiers = true;
  byMobile = false;
  includeMerged = false;
  candidates: any[] = [];
  total = 0;
  page = 1;
  perPage = 10;
  selectedSources: string[] = [];
  searchTimeout: any = null;

  get canMerge(): boolean {
    return this.selectedSources.length > 0;
  }

  get allSelected(): boolean {
    return this.candidates.length > 0 && this.selectedSources.length === this.candidates.filter(c => !c.deleted).length;
  }

  isSelected(uuid: string): boolean {
    return this.selectedSources.indexOf(uuid) !== -1;
  }

  toggleAll() {
    if (this.allSelected) {
      this.selectedSources = [];
    } else {
      this.selectedSources = this.candidates.filter(c => !c.deleted).map(c => c.uuid);
    }
  }

  truncateIds(ids: string[] | undefined): string[] {
    if (!ids) return [];
    return ids.slice(0, 2);
  }

  formatDate(iso: string | null): string {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR");
  }

  reset() {
    this.candidates = [];
    this.total = 0;
    this.page = 1;
    this.selectedSources = [];
  }

  onShow() {
    this.reset();
    this.loadSimilar();
  }

  onCriteriaChange() {
    this.page = 1;
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => this.loadSimilar(), 200);
  }

  onPageChange(page: number) {
    this.page = page;
    this.loadSimilar();
  }

  async loadSimilar() {
    if (!this.byIdentifiers && !this.byMobile) {
      this.candidates = [];
      this.total = 0;
      return;
    }
    this.loading = true;
    try {
      const res = await api.get(api.core, "contract/similar/" + this.contractUuid, {
        params: {
          byIdentifiers: this.byIdentifiers ? 1 : 0,
          byMobile: this.byMobile ? 1 : 0,
          includeMerged: this.includeMerged ? 1 : 0,
          page: this.page,
        },
      });
      if (res?.data?.status === "success") {
        this.candidates = res.data.candidates || [];
        this.total = res.data.total || 0;
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.loading = false;
    }
  }

  async confirmMerge() {
    if (!this.canMerge) return;
    const list = this.selectedSources
        .map(uuid => this.candidates.find(c => c.uuid === uuid))
        .map(c => `<li><code>${c?.police || c?.uuid}</code> — ${c?.subscriber?.name || '—'}</li>`)
        .join("");
    const result = await Swal.fire({
      title: "Confirmer la fusion ?",
      html: `Master : <code>${this.contractUuid}</code><br/>Sources fusionnées :<ul class='text-start'>${list}</ul>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Fusionner",
      cancelButtonText: "Annuler",
      confirmButtonColor: "#d33",
    });
    if (!result.isConfirmed) return;

    this.merging = true;
    try {
      const res = await api.post(api.core, "contract/merge-manual", {
        masterUuid: this.contractUuid,
        sourceUuids: this.selectedSources,
      });
      if (res?.data?.status === "success") {
        const report = res.data.report;
        const errors = report.errors || [];
        const merged = report.merged || [];
        let msg = `${merged.length} contrat(s) fusionné(s).`;
        if (errors.length) {
          msg += `<br/><br/><b class='text-danger'>${errors.length} erreur(s) :</b><ul class='text-start'>`
              + errors.map((e: any) => `<li><code>${e.uuid}</code>: ${e.reason}</li>`).join("") + "</ul>";
        }
        await Swal.fire({
          title: "Fusion terminée",
          html: msg,
          icon: errors.length ? "warning" : "success",
        });
        (this.$refs.modal as any).hide();
        this.$emit("merged", {masterUuid: this.contractUuid, report});
      } else {
        await Swal.fire({title: "Erreur", text: res?.data?.message || "Fusion échouée", icon: "error"});
      }
    } catch (e: any) {
      await Swal.fire({title: "Erreur", text: e?.response?.data?.message || e?.message || "Fusion échouée", icon: "error"});
    } finally {
      this.merging = false;
    }
  }
}
</script>
